/**
 * localStorage writes that survive a full quota.
 *
 * The browser gives this origin ~5MB shared across every gnq_* key. The
 * largest writer by far is gnq_report_data_<reportId> — a full assessmentData
 * payload (~90KB per 6-section run) written on every completed run and never
 * pruned. Once the quota filled, the next unguarded setItem threw
 * QuotaExceededError from inside a passive effect, which unmounted the app:
 *
 *   commitHookEffectListMount -> commitPassiveMountEffects -> commitRoot
 *
 * safeSetItem never throws. On a quota error it evicts cached report payloads
 * oldest-first and retries, so a full store self-heals on the next write
 * instead of white-screening.
 *
 * Deliberately NOT evicted: gnq_documents. It caches extracted document text
 * that is sent to the pipeline on run (App.jsx uploaded_documents), so
 * discarding it would silently degrade a later run rather than just losing
 * workspace history. If eviction cannot free enough room we warn and skip the
 * write — the app keeps working, only the history entry is lost.
 */

const REPORT_DATA_PREFIX = "gnq_report_data_";

// reportId is `${therapyName}-${Date.now()}`, so the trailing digits are the
// creation time. Recover it to evict genuinely oldest-first.
function reportTimestamp(key) {
  const m = key.match(/-(\d{10,})$/);
  return m ? Number(m[1]) : 0;
}

function cachedReportKeysOldestFirst() {
  return Object.keys(localStorage)
    .filter((k) => k.startsWith(REPORT_DATA_PREFIX))
    .sort((a, b) => reportTimestamp(a) - reportTimestamp(b));
}

function isQuotaError(e) {
  return (
    e instanceof DOMException &&
    (e.name === "QuotaExceededError" ||
      e.name === "NS_ERROR_DOM_QUOTA_REACHED" || // Firefox
      e.code === 22 ||
      e.code === 1014)
  );
}

/**
 * Write to localStorage, recovering from a full quota. Returns true if the
 * value was stored. Never throws.
 */
export function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    if (!isQuotaError(e)) {
      console.warn(`[GNQ] Could not write ${key}:`, e);
      return false;
    }
  }

  // Quota is full. Evict cached report payloads oldest-first, retrying after
  // each removal so we free the minimum needed. The workspace report LIST
  // (gnq_reports) is small metadata and is untouched — only the cached body of
  // an old report goes, and that only affects re-opening it from the sidebar.
  for (const stale of cachedReportKeysOldestFirst()) {
    if (stale === key) continue;
    localStorage.removeItem(stale);
    try {
      localStorage.setItem(key, value);
      console.warn(
        `[GNQ] localStorage was full — evicted cached report ${stale} to save ${key}.`
      );
      return true;
    } catch (e) {
      if (!isQuotaError(e)) {
        console.warn(`[GNQ] Could not write ${key}:`, e);
        return false;
      }
    }
  }

  console.warn(
    `[GNQ] localStorage is full and no cached reports remain to evict — ` +
      `${key} was not saved. The app and pipeline runs are unaffected; ` +
      `workspace history may be incomplete until documents are removed.`
  );
  return false;
}

export default safeSetItem;
